import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, TrendingUp, Search, Users, BarChart2, AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import LiveDate from '../components/LiveDate';
import './KeralaAnalysis.css';

const DISTRICTS = [
  { name: 'Thiruvananthapuram', rank: '#1', opportunity: 'High', pop: '3.3M', aeoAdoption: 'Low (8%)', topQueries: ['IT consultant Trivandrum', 'digital marketing Trivandrum', 'SEO expert Thiruvananthapuram'], color: '#00d4ff' },
  { name: 'Ernakulam (Kochi)', rank: '#2', opportunity: 'Very High', pop: '3.2M', aeoAdoption: 'Very Low (5%)', topQueries: ['best digital agency Kochi', 'SEO company Ernakulam', 'AI marketing Kochi'], color: '#8b5cf6' },
  { name: 'Kozhikode', rank: '#3', opportunity: 'High', pop: '3.1M', aeoAdoption: 'Minimal (3%)', topQueries: ['SEO expert Kozhikode', 'digital marketing Calicut', 'IT services Kozhikode'], color: '#ec4899' },
  { name: 'Thrissur', rank: '#4', opportunity: 'Medium-High', pop: '3.1M', aeoAdoption: 'Minimal (3%)', topQueries: ['digital marketing Thrissur', 'website development Thrissur', 'SEO Thrissur'], color: '#f59e0b' },
  { name: 'Malappuram', rank: '#5', opportunity: 'Medium', pop: '4.1M', aeoAdoption: 'None (1%)', topQueries: ['digital marketing Malappuram', 'SEO Malappuram', 'website Malappuram'], color: '#10b981' },
  { name: 'Kannur', rank: '#6', opportunity: 'Medium', pop: '2.5M', aeoAdoption: 'None (1%)', topQueries: ['SEO Kannur', 'digital marketing Kannur', 'IT company Kannur'], color: '#00d4ff' },
  { name: 'Kollam', rank: '#7', opportunity: 'Medium', pop: '2.6M', aeoAdoption: 'None (1%)', topQueries: ['digital marketing Kollam', 'SEO Kollam', 'website design Kollam'], color: '#8b5cf6' },
  { name: 'Alappuzha', rank: '#8', opportunity: 'Niche', pop: '2.1M', aeoAdoption: 'None (<1%)', topQueries: ['tourism SEO Alleppey', 'digital marketing Alappuzha', 'houseboat SEO Kerala'], color: '#ec4899' },
  { name: 'Palakkad', rank: '#9', opportunity: 'Low-Medium', pop: '2.8M', aeoAdoption: 'None (<1%)', topQueries: ['SEO Palakkad', 'digital marketing Palakkad', 'IT services Palakkad'], color: '#f59e0b' },
  { name: 'Kottayam', rank: '#10', opportunity: 'Medium', pop: '2.0M', aeoAdoption: 'None (<1%)', topQueries: ['digital marketing Kottayam', 'SEO Kottayam', 'website development Kottayam'], color: '#10b981' },
  { name: 'Wayanad', rank: '#11', opportunity: 'Niche', pop: '0.8M', aeoAdoption: 'None (<1%)', topQueries: ['tourism SEO Wayanad', 'resort marketing Wayanad', 'eco-tourism digital marketing'], color: '#00d4ff' },
  { name: 'Idukki', rank: '#12', opportunity: 'Niche', pop: '1.1M', aeoAdoption: 'None (<1%)', topQueries: ['tea estate marketing', 'tourism SEO Munnar', 'digital marketing Idukki'], color: '#8b5cf6' },
  { name: 'Kasaragod', rank: '#13', opportunity: 'Low-Medium', pop: '1.3M', aeoAdoption: 'None (<1%)', topQueries: ['digital marketing Kasaragod', 'SEO Kasaragod', 'IT services Kasaragod'], color: '#ec4899' },
  { name: 'Pathanamthitta', rank: '#14', opportunity: 'Low', pop: '1.2M', aeoAdoption: 'None (<1%)', topQueries: ['digital marketing Pathanamthitta', 'SEO Pathanamthitta', 'diaspora marketing Kerala'], color: '#f59e0b' },
];

const SEARCH_TERMS = [
  'AEO Kerala', 'answer engine optimization Kerala', 'GEO optimization Kerala',
  'AI search optimization Kerala', 'ChatGPT optimization Kerala', 'generative engine optimization India',
  'Gemini AI optimization', 'Perplexity SEO Kerala', 'LLM optimization service India',
  'AI visibility expert Kerala', 'AEO expert Trivandrum', 'AEO services Kochi',
  'answer engine optimization Kozhikode', 'AI SEO Kerala 2025', 'get found in ChatGPT',
  'optimize for AI search', 'LLMO service India', 'GEO vs SEO Kerala',
  'AI optimization agency Kerala', 'best AEO consultant India',
];

const KeralaAnalysis = () => {
  return (
    <>
      <Helmet>
        <title>Kerala AI Optimization Market Analysis | AEO & GEO District-wise Report | Rajesh R</title>
        <meta name="description" content="Detailed analysis of AI/AEO optimization adoption across all 14 Kerala districts. Market opportunity assessment, competitor landscape, and search term analysis for 2025." />
        <meta name="keywords" content="AEO Kerala districts, AI optimization Kerala market, GEO Kerala analysis, digital marketing Kerala 2025, AI search Kerala" />
      </Helmet>

      <div className="page-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="sp-live-bar">
              <span className="live-dot"></span>
              <span>Live Market Data — Updated: </span>
              <LiveDate showTime className="hero-live-date" />
            </div>
            <div className="badge" style={{ marginBottom: '16px' }}><BarChart2 size={12} /> Kerala Market Intelligence</div>
            <h1 className="section-title" style={{ fontSize: 'clamp(28px,4vw,48px)', marginTop: '16px' }}>
              AI Optimization Landscape: <span className="gradient-text">Kerala 2025</span>
            </h1>
            <p className="section-subtitle">
              Deep research into the current state of AEO, GEO, and AI visibility optimization adoption across all 14 Kerala districts — identifying competitor gaps and market opportunities.
            </p>
          </motion.div>
        </div>
      </div>

      {/* MARKET OVERVIEW */}
      <section className="ka-section">
        <div className="container">
          <div className="ka-overview-grid">
            {[
              { icon: <Users size={20} />, val: '35.1M+', label: 'Kerala Internet Users', sub: 'Potential AI search audience' },
              { icon: <Search size={20} />, val: '~8%', label: 'AEO Adoption Rate', sub: 'Across all Kerala businesses' },
              { icon: <TrendingUp size={20} />, val: '340%', label: 'AI Search Growth', sub: 'Year-over-year in Kerala' },
              { icon: <AlertTriangle size={20} />, val: '92%', label: 'Opportunity Gap', sub: 'Businesses not yet AI-optimized' },
            ].map((s, i) => (
              <motion.div key={s.label} className="ka-stat-card"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="ka-stat-icon">{s.icon}</div>
                <div className="stat-number" style={{ fontSize: '36px' }}>{s.val}</div>
                <div className="ka-stat-label">{s.label}</div>
                <div className="ka-stat-sub">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY FINDING */}
      <section className="ka-section" style={{ background: 'var(--bg-secondary)', padding: '40px 0' }}>
        <div className="container">
          <motion.div className="ka-finding-box" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="ka-finding-icon"><Zap size={24} color="var(--accent-cyan)" /></div>
            <div>
              <h3>Key Market Finding — February 2025</h3>
              <p>
                Research across all 14 Kerala districts reveals that <strong>fewer than 12 businesses</strong> in the entire state have any dedicated AEO or GEO strategy. 
                Zero businesses in districts like Wayanad, Idukki, Kasaragod, and Pathanamthitta have any AI visibility optimization. 
                This represents an <strong>unprecedented first-mover opportunity</strong> — comparable to being one of the first businesses in Kerala to do SEO in 2010.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DISTRICT ANALYSIS */}
      <section className="ka-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}><MapPin size={12} /> District-wise Analysis</div>
            <h2 className="section-title">
              AEO Adoption by <span className="gradient-text">Kerala District</span>
            </h2>
            <p className="section-subtitle">Current AI/AEO optimization landscape as of <LiveDate /></p>
          </motion.div>
          <div className="district-grid">
            {DISTRICTS.map((d, i) => (
              <motion.div key={d.name} className="district-card"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }} viewport={{ once: true }}>
                <div className="district-header">
                  <div className="district-rank" style={{ color: d.color }}>{d.rank}</div>
                  <div>
                    <h3 className="district-name">{d.name}</h3>
                    <div className="district-pop">Population: {d.pop}</div>
                  </div>
                  <div className={`district-opportunity opportunity-${d.opportunity.split(' ')[0].toLowerCase()}`}>
                    {d.opportunity}
                  </div>
                </div>
                <div className="district-aeo">
                  <span className="district-label">AEO Adoption:</span>
                  <span className="district-value" style={{ color: d.aeoAdoption === 'Low (8%)' ? 'var(--accent-orange)' : d.aeoAdoption === 'Very Low (5%)' ? 'var(--accent-orange)' : 'var(--accent-green)' }}>
                    {d.aeoAdoption}
                  </span>
                </div>
                <div className="district-queries">
                  <span className="district-label">Top AI Queries:</span>
                  {d.topQueries.map(q => <span key={q} className="query-tag">{q}</span>)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH TERMS */}
      <section className="ka-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}><Search size={12} /> SEO-Optimized Search Terms</div>
            <h2 className="section-title">
              High-Value <span className="gradient-text">Search Terms</span> We Target
            </h2>
            <p className="section-subtitle">These are the exact queries Kerala businesses and their clients search for — all optimized on this site for maximum AI and Google visibility.</p>
          </motion.div>
          <div className="search-terms-grid">
            {SEARCH_TERMS.map((term, i) => (
              <motion.div key={term} className="search-term-card"
                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.03 }} viewport={{ once: true }}>
                <Search size={12} />
                <span>{term}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPETITOR ANALYSIS */}
      <section className="ka-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}>Competitive Landscape</div>
            <h2 className="section-title">
              Who's Ranking for <span className="gradient-text">AI Optimization</span> in Kerala?
            </h2>
          </motion.div>
          <div className="competitor-table">
            <div className="comp-table-header">
              <span>Provider Type</span>
              <span>Traditional SEO</span>
              <span>AEO/GEO</span>
              <span>AI Platform</span>
              <span>Schema/Structured Data</span>
            </div>
            {[
              { type: 'Large Kerala Digital Agencies', seo: '✅', aeo: '❌', ai: '❌', schema: '⚠️' },
              { type: 'Freelance SEO Experts', seo: '✅', aeo: '❌', ai: '❌', schema: '⚠️' },
              { type: 'National SEO Firms (India)', seo: '✅', aeo: '⚠️', ai: '❌', schema: '✅' },
              { type: 'Rajesh R (This Service)', seo: '✅', aeo: '✅', ai: '✅', schema: '✅' },
            ].map((row, i) => (
              <motion.div key={row.type} className={`comp-table-row ${row.type.includes('Rajesh') ? 'highlight-row' : ''}`}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <span className="comp-type">{row.type}</span>
                <span>{row.seo}</span>
                <span>{row.aeo}</span>
                <span>{row.ai}</span>
                <span>{row.schema}</span>
              </motion.div>
            ))}
          </div>
          <div className="comp-legend">
            <span>✅ Fully offered</span>
            <span>⚠️ Partially</span>
            <span>❌ Not offered</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ka-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}><CheckCircle size={12} color="var(--accent-green)" /> Claim Your District's AI Visibility</div>
            <h2 className="section-title">
              Be the First AI-Optimized Business <br />in <span className="gradient-text">Your Kerala District</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '16px auto 32px' }}>
              The data is clear: 92% of Kerala businesses have zero AI visibility strategy. 
              The businesses that act now will own AI search in their market for years.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20read%20the%20Kerala%20AI%20Market%20Analysis%20on%20your%20site%20and%20I%20want%20to%20be%20the%20FIRST%20AI-optimized%20business%20in%20my%20district.%20Let%E2%80%99s%20talk!"
                target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: '16px 32px' }}>
                <Zap size={18} /> Claim Your District Now
              </a>
              <a href="tel:+917907038984" className="btn-secondary" style={{ padding: '15px 32px' }}>
                Call: +91 79070 38984
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default KeralaAnalysis;
